import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, Menu, X, User, ChevronDown } from "lucide-react";
import { useCart } from "@/lib/cart";
import { useCustomerAuth } from "@/lib/customerAuth";
import { CATEGORIES, LOGO_URL } from "@/lib/api";
import { SUB_COLLECTIONS } from "@/lib/beddingConfig";

export default function Header() {
  const { count, setDrawerOpen } = useCart();
  const { customer } = useCustomerAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // slug currently hovered
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  // Stop the page behind the mobile menu from scrolling
  useEffect(() => {
    document.body.classList.toggle("no-scroll", mobileOpen);
    return () => document.body.classList.remove("no-scroll");
  }, [mobileOpen]);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/category/bedding?search=${encodeURIComponent(q.trim())}`);
      setSearchOpen(false);
      setQ("");
    }
  };

  return (
    <header
      className="bg-cream/85 backdrop-blur-xl backdrop-saturate-150 border-b border-border-subtle"
      data-testid="site-header"
      onMouseLeave={() => setOpenMenu(null)}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <button
            className="lg:hidden p-2 -ml-2 text-ink tap-target"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
            <img src={LOGO_URL} alt="Bedding Lounge" className="h-10 w-10 sm:h-12 sm:w-12 object-contain" loading="eager" />
            <div className="hidden sm:block leading-none">
              <div className="font-display text-2xl tracking-tight text-ink">Bedding Lounge</div>
              <div className="label-eyebrow text-[10px] mt-1">Soft Luxury · Nature</div>
            </div>
          </Link>

          {/* Nav */}
          <nav className="hidden lg:flex items-stretch gap-8" data-testid="primary-nav">
            {CATEGORIES.map((c) => {
              const subs = SUB_COLLECTIONS[c.slug];
              const hasMega = subs && subs.length > 0;
              return (
                <div
                  key={c.slug}
                  className="relative flex items-center"
                  onMouseEnter={() => hasMega && setOpenMenu(c.slug)}
                >
                  <Link
                    to={`/category/${c.slug}`}
                    onClick={() => setOpenMenu(null)}
                    className="inline-flex items-center gap-1 text-sm tracking-wide text-ink hover:text-sage transition-colors duration-300"
                    data-testid={`nav-${c.slug}`}
                  >
                    {c.label}
                    {hasMega && <ChevronDown size={12} className="opacity-60" />}
                  </Link>
                </div>
              );
            })}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              className="p-2 text-ink hover:text-sage transition-colors tap-target"
              onClick={() => setSearchOpen((v) => !v)}
              data-testid="search-toggle"
              aria-label="Search"
            >
              <Search size={20} />
            </button>
            <Link
              to={customer ? "/account" : "/login"}
              className="p-2 text-ink hover:text-sage transition-colors hidden sm:inline-flex tap-target"
              data-testid="header-account"
              aria-label={customer ? "My Account" : "Sign in"}
              title={customer ? `${customer.full_name}` : "Sign in / Register"}
            >
              <User size={20} />
            </Link>
            <button
              className="relative p-2 text-ink hover:text-sage transition-colors tap-target"
              onClick={() => setDrawerOpen(true)}
              data-testid="cart-toggle"
              aria-label="Cart"
            >
              <ShoppingBag size={20} />
              {count > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 bg-rose text-cream text-[10px] font-semibold rounded-full h-5 min-w-5 px-1 flex items-center justify-center"
                  data-testid="cart-count-badge"
                >
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Mega menu dropdown (desktop) */}
        {openMenu && SUB_COLLECTIONS[openMenu] && (
          <div
            className="hidden lg:block absolute inset-x-0 top-full bg-cream border-t border-border-subtle shadow-xl"
            data-testid={`mega-menu-${openMenu}`}
            onMouseLeave={() => setOpenMenu(null)}
          >
            <div className="max-w-7xl mx-auto px-8 py-10">
              <div className="grid grid-cols-12 gap-8">
                {/* Heading column */}
                <div className="col-span-3">
                  <div className="label-eyebrow !text-[10px] mb-2">Shop by Type</div>
                  <h3 className="font-display text-3xl text-ink leading-tight">
                    {CATEGORIES.find((c) => c.slug === openMenu)?.label}
                  </h3>
                  <Link
                    to={`/category/${openMenu}`}
                    onClick={() => setOpenMenu(null)}
                    className="inline-block mt-4 text-xs uppercase tracking-[0.22em] text-sage hover:text-sage-dark font-semibold border-b border-sage pb-0.5"
                    data-testid={`mega-view-all-${openMenu}`}
                  >
                    View All →
                  </Link>
                </div>

                {/* Sub-collection columns */}
                <div className="col-span-9 grid grid-cols-2 md:grid-cols-4 gap-6">
                  {SUB_COLLECTIONS[openMenu].map((s) => (
                    <Link
                      key={s.slug}
                      to={`/category/${openMenu}?sub=${s.slug}`}
                      onClick={() => setOpenMenu(null)}
                      className="group flex flex-col"
                      data-testid={`mega-sub-${s.slug}`}
                    >
                      <div className="aspect-[4/5] overflow-hidden bg-cream-warm mb-3">
                        <img
                          src={s.image}
                          alt={s.label}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="font-display text-lg text-ink group-hover:text-sage transition-colors">
                        {s.label}
                      </div>
                      <div className="text-xs text-ink-soft mt-1 leading-snug">{s.description}</div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search expansion */}
        {searchOpen && (
          <form onSubmit={onSearch} className="pb-4" data-testid="search-form">
            <div className="flex items-center border-b border-ink/30 py-2">
              <Search size={18} className="text-ink-soft" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search bedding, blankets, curtains…"
                className="flex-1 bg-transparent outline-none px-3 text-ink placeholder:text-ink-soft/60 font-sans"
                data-testid="search-input"
              />
              <button type="submit" className="text-xs uppercase tracking-widest text-sage font-semibold" data-testid="search-submit">
                Search
              </button>
            </div>
          </form>
        )}

        {/* Mobile nav */}
        {mobileOpen && (
          <div
            className="lg:hidden border-t border-border-subtle pt-1 pb-[max(1rem,env(safe-area-inset-bottom))] max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain"
            data-testid="mobile-nav"
          >
            <div className="flex flex-col">
              {CATEGORIES.map((c) => {
                const subs = SUB_COLLECTIONS[c.slug];
                return (
                  <div key={c.slug} className="border-b border-border-subtle last:border-0">
                    <Link
                      to={`/category/${c.slug}`}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center min-h-[52px] py-3 text-base text-ink font-sans"
                      data-testid={`mobile-nav-${c.slug}`}
                    >
                      {c.label}
                    </Link>
                    {subs && (
                      <div className="pl-4 pb-2">
                        {subs.map((s) => (
                          <Link
                            key={s.slug}
                            to={`/category/${c.slug}?sub=${s.slug}`}
                            onClick={() => setMobileOpen(false)}
                            className="flex items-center min-h-[46px] text-sm text-ink-soft active:text-sage"
                            data-testid={`mobile-sub-${s.slug}`}
                          >
                            — {s.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              <Link
                to={customer ? "/account" : "/login"}
                onClick={() => setMobileOpen(false)}
                className="flex items-center min-h-[52px] text-sage font-semibold uppercase tracking-widest text-xs"
                data-testid="mobile-account-link"
              >
                {customer ? "My Account" : "Sign in / Create Account"}
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
